import { useState, useEffect, useCallback } from 'react';
import { sightingAPI } from '../services/api';
import { createLogger } from '../utils/logger';

const logger = createLogger('useUserStats');

/**
 * useUserStats - Custom hook to fetch user statistics
 * Returns loading state, error state, stats data, and refetch function
 * 
 * Stats include:
 * - totalSightings: Number of sightings reported by user
 * - confirmationsGiven: Number of confirmations user has made
 * - pendingConfirmations: Number of pending confirmations available (optionally filtered by location)
 * 
 * @param {string} userId - The user ID to fetch stats for
 * @param {boolean} autoFetch - Whether to automatically fetch on mount (default: true)
 * @param {Object} userLocation - Optional user location {lat, lng} for proximity-based filtering
 */
export function useUserStats(userId, autoFetch = true, userLocation = null) {
  const [stats, setStats] = useState({
    totalSightings: 0,
    confirmationsGiven: 0,
    pendingConfirmations: 0
  });
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Build params with optional location filtering
      const params = {};
      if (userLocation?.lat && userLocation?.lng) {
        params.lat = userLocation.lat;
        params.lng = userLocation.lng;
        params.radius = 50000; // 50km radius
      }

      // Use the new dedicated stats endpoint
      const statsResponse = await sightingAPI.getUserStats(userId, params);
      const statsData = statsResponse.data || {};

      setStats({
        totalSightings: statsData.totalSightings || 0,
        confirmationsGiven: statsData.confirmationsGiven || 0,
        pendingConfirmations: statsData.pendingConfirmations || 0
      });
    } catch (err) {
      logger.error('Failed to fetch user stats', err);
      setError(err.response?.data?.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, [userId, userLocation]);

  useEffect(() => {
    if (autoFetch) {
      fetchStats();
    }
  }, [autoFetch, fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats
  };
}
